import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, orderBy, limit, onSnapshot, serverTimestamp, doc, updateDoc, increment, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';

export function useFeed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'posts'), orderBy('createdAt', 'desc'), limit(30));
    const unsub = onSnapshot(q, (snap) => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, []);

  const likePost = useCallback(async (postId) => {
    if (!user) return;
    const postRef = doc(db, 'posts', postId);
    const postSnap = await getDoc(postRef);
    const likes = postSnap.data()?.likedBy || [];
    if (likes.includes(user.uid)) {
      await updateDoc(postRef, { likes: increment(-1), likedBy: arrayRemove(user.uid) });
    } else {
      await updateDoc(postRef, { likes: increment(1), likedBy: arrayUnion(user.uid) });
    }
  }, [user]);

  const createPost = useCallback(async ({ caption, videoFile, thumbnailEmoji, tags, country }) => {
    if (!user) return;
    let videoURL = null;
    if (videoFile) {
      const fileRef = ref(storage, `videos/${user.uid}/${Date.now()}`);
      const uploadTask = uploadBytesResumable(fileRef, videoFile);
      await new Promise((resolve, reject) => {
        uploadTask.on('state_changed', null, reject, async () => {
          videoURL = await getDownloadURL(uploadTask.snapshot.ref);
          resolve();
        });
      });
    }
    await addDoc(collection(db, 'posts'), {
      caption, videoURL, thumbnailEmoji: thumbnailEmoji || '🎬',
      tags: tags || [], country: country || 'Uganda',
      authorId: user.uid, authorName: user.displayName, authorPhoto: user.photoURL || null,
      likes: 0, likedBy: [], comments: 0, shares: 0, views: 0, createdAt: serverTimestamp()
    });
  }, [user]);

  return { posts, loading, likePost, createPost };
}

export function useComments(postId) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);

  useEffect(() => {
    if (!postId) return;
    const q = query(collection(db, 'posts', postId, 'comments'), orderBy('createdAt', 'asc'), limit(50));
    const unsub = onSnapshot(q, (snap) => {
      setComments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [postId]);

  const addComment = useCallback(async (text) => {
    if (!user || !postId || !text.trim()) return;
    await addDoc(collection(db, 'posts', postId, 'comments'), {
      text: text.trim(), authorId: user.uid, authorName: user.displayName,
      authorPhoto: user.photoURL || null, createdAt: serverTimestamp()
    });
    await updateDoc(doc(db, 'posts', postId), { comments: increment(1) });
  }, [user, postId]);

  return { comments, addComment };
}
